import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import evaa_logo_text_v2 from '../assets/evaa_logo_text_v2.svg'; // Import the logo
import evaa_logo_picto from '../assets/evaa_logo_picto.svg';

const EvaaXPage = () => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Mouse tracking for logo interaction
  useEffect(() => {
    const handleMouseMove = (e) => {
      const mouseX = (e.clientX / window.innerWidth - 0.5) * 20;
      const mouseY = (e.clientY / window.innerHeight - 0.5) * 20;
      setMousePos({ x: mouseX, y: mouseY });
    };

    const handleMouseLeave = () => {
      setMousePos({ x: 0, y: 0 });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  // Dynamic particle creation
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const createParticle = () => {
      const id = Date.now() + Math.random();
      const newParticle = {
        id,
        size: Math.random() * 4 + 1,
        left: Math.random() * 100,
        top: Math.random() * 100,
        delay: Math.random() * 6,
      };

      setParticles(prev => [...prev, newParticle]);

      setTimeout(() => {
        setParticles(prev => prev.filter(p => p.id !== id));
      }, 6000);
    };

    const interval = setInterval(createParticle, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleCTAClick = (e) => {
    // Create ripple effect
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    const ripple = document.createElement('div');
    
    ripple.className = 'absolute bg-white rounded-full opacity-25 pointer-events-none';
    ripple.style.width = ripple.style.height = '100px';
    ripple.style.left = e.clientX - rect.left - 50 + 'px';
    ripple.style.top = e.clientY - rect.top - 50 + 'px';
    ripple.style.animation = 'ping 0.6s ease-out';
    
    button.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);
    
    // Navigate to your Evaa-X app
    setTimeout(() => {
        window.location.href = 'https://evaa-x.space/';
    }, 300);
  };

  return (
    <>
      <style jsx>{`
        .dimensional-bg {
          background: linear-gradient(-45deg, #0f172a, #1e293b, #334155, #475569);
          background-size: 400% 400%;
          animation: gradientShift 15s ease infinite;
        }

        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .particle {
          position: absolute;
          background: rgba(52, 104, 26, 0.3);
          border-radius: 50%;
          animation: float 6s ease-in-out infinite;
          pointer-events: none;
        }

        .particle:nth-child(even) {
          background: rgba(148, 163, 184, 0.2);
          animation-delay: -2s;
        }

        .particle:nth-child(3n) {
          animation-delay: -4s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.7; }
          50% { transform: translateY(-20px) rotate(180deg); opacity: 1; }
        }

        .morph-logo {
          animation: morph 8s ease-in-out infinite;
          transform-origin: center;
          transition: transform 0.3s ease;
        }

        @keyframes morph {
          0%, 100% { 
            transform: scale(1) rotate(0deg);
            filter: hue-rotate(0deg) brightness(1);
          }
          25% { 
            transform: scale(1.1) rotate(90deg);
            filter: hue-rotate(30deg) brightness(1.2);
          }
          50% { 
            transform: scale(0.9) rotate(180deg);
            filter: hue-rotate(60deg) brightness(1.4);
          }
          75% { 
            transform: scale(1.05) rotate(270deg);
            filter: hue-rotate(30deg) brightness(1.1);
          }
        }

        .data-flow {
          position: absolute;
          width: 2px;
          height: 20px;
          background: linear-gradient(to bottom, transparent, #34681A, transparent);
          animation: dataFlow 3s linear infinite;
          pointer-events: none;
        }

        @keyframes dataFlow {
          0% { transform: translateY(-100vh) scaleY(0); opacity: 0; }
          10% { opacity: 1; transform: scaleY(1); }
          90% { opacity: 1; transform: scaleY(1); }
          100% { transform: translateY(100vh) scaleY(0); opacity: 0; }
        }

        .pulse-border {
          animation: pulseBorder 2s ease-in-out infinite;
        }

        @keyframes pulseBorder {
          0%, 100% { box-shadow: 0 0 0 0 rgba(52, 104, 26, 0.7); }
          50% { box-shadow: 0 0 0 20px rgba(52, 104, 26, 0); }
        }

        .text-appear {
          animation: textAppear 1s ease-out forwards;
          opacity: 0;
          transform: translateY(30px);
        }

        .text-appear-delay-1 { animation-delay: 0.2s; }
        .text-appear-delay-2 { animation-delay: 0.4s; }
        .text-appear-delay-3 { animation-delay: 0.6s; }
        .text-appear-delay-4 { animation-delay: 0.8s; }

        @keyframes textAppear {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .glitch {
          position: relative;
          color: #94a3b8;
        }

        .glitch::before,
        .glitch::after {
          content: attr(data-text);
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }

        .glitch::before {
          animation: glitch 2s infinite;
          color: #34681A;
          z-index: -1;
        }

        .glitch::after {
          animation: glitch 2s infinite;
          color: #ef4444;
          z-index: -2;
        }

        @keyframes glitch {
          0%, 100% { transform: translate(0); }
          20% { transform: translate(-2px, 2px); }
          40% { transform: translate(-2px, -2px); }
          60% { transform: translate(2px, 2px); }
          80% { transform: translate(2px, -2px); }
        }

        .card-3d {
          transform-style: preserve-3d;
          transition: transform 0.3s ease;
        }

        .card-3d:hover {
          transform: rotateY(5deg) rotateX(5deg) scale(1.02);
        }
      `}</style>

      <div className="dimensional-bg min-h-screen relative overflow-hidden font-sans">
        {/* Static Floating Particles */}
        <div className="particle w-2 h-2" style={{ top: '20%', left: '10%', animationDelay: '0s' }}></div>
        <div className="particle w-3 h-3" style={{ top: '60%', left: '85%', animationDelay: '-1s' }}></div>
        <div className="particle w-1 h-1" style={{ top: '40%', left: '20%', animationDelay: '-3s' }}></div>
        <div className="particle w-2 h-2" style={{ top: '80%', left: '70%', animationDelay: '-5s' }}></div>
        <div className="particle w-1 h-1" style={{ top: '15%', left: '60%', animationDelay: '-2s' }}></div>
        <div className="particle w-2 h-2" style={{ top: '70%', left: '30%', animationDelay: '-4s' }}></div>

        {/* Dynamic Particles */}
        {particles.map(particle => (
          <div
            key={particle.id}
            className="particle"
            style={{
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              top: `${particle.top}%`,
              left: `${particle.left}%`,
              animationDelay: `${particle.delay}s`
            }}
          ></div>
        ))}

        {/* Data Flow Lines */}
        <div className="data-flow" style={{ left: '15%', animationDelay: '0s' }}></div>
        <div className="data-flow" style={{ left: '35%', animationDelay: '-1s' }}></div>
        <div className="data-flow" style={{ left: '55%', animationDelay: '-2s' }}></div>
        <div className="data-flow" style={{ left: '75%', animationDelay: '-0.5s' }}></div>
        <div className="data-flow" style={{ left: '85%', animationDelay: '-1.5s' }}></div>

        <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Animated Logo Picto */}
            <div className="mb-8 text-appear">
              <div 
                className="morph-logo inline-block"
                style={{
                  transform: `translate(${mousePos.x}px, ${mousePos.y}px) scale(1.1)`
                }}
              >
                <img 
                  src={evaa_logo_picto} 
                  alt="Evaa Logo"
                  className="w-150 h-150 mx-auto"
                />
              </div>
            </div>

            {/* Logo Text */}
            <div className="mb-8 text-appear text-appear-delay-1">
                <Link to="/" className="inline-block">
                    <img 
                        src={evaa_logo_text_v2} 
                        alt="EVAA-X"
                        className="h-16 md:h-24 mx-auto mb-2 brightness-0 invert hover:scale-105 transition-transform duration-300"
                    />
                </Link>
              <div className="text-lg md:text-xl font-mono text-slate-400 tracking-widest">
                DATA MANAGEMENT SYSTEM
              </div>
            </div>

            {/* Main Heading */}
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 text-appear text-appear-delay-2">
              Propulse Your Surveys Into
              <br/>
              <span className="bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent">
                Another Dimension
              </span>
            </h2>

            {/* Glitch Subheading */}
            <p 
              className="text-xl md:text-2xl mb-12 text-appear text-appear-delay-3 glitch" 
              data-text="Your entire data ecosystem in the palm of your hand"
            >
              Your entire data ecosystem in the palm of your hand
            </p>

            {/* Feature Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-12 text-appear text-appear-delay-4">
              <div className="card-3d bg-slate-800/30 backdrop-blur-sm border border-slate-600 rounded-xl p-6 hover:border-green-500/50 transition-all duration-300">
                <div className="text-green-400 text-3xl mb-4">∞</div>
                <h3 className="text-white font-semibold text-lg mb-2">Multi-Dimensional</h3>
                <p className="text-slate-300 text-sm">Transform flat data into rich, interconnected insights</p>
              </div>
              
              <div className="card-3d bg-slate-800/30 backdrop-blur-sm border border-slate-600 rounded-xl p-6 hover:border-green-500/50 transition-all duration-300">
                <div className="text-green-400 text-3xl mb-4">⚡</div>
                <h3 className="text-white font-semibold text-lg mb-2">Accelerated Analytics</h3>
                <p className="text-slate-300 text-sm">Process and analyze at lightspeed</p>
              </div>
              
              <div className="card-3d bg-slate-800/30 backdrop-blur-sm border border-slate-600 rounded-xl p-6 hover:border-green-500/50 transition-all duration-300">
                <div className="text-green-400 text-3xl mb-4">🌐</div>
                <h3 className="text-white font-semibold text-lg mb-2">Ecosystem Control</h3>
                <p className="text-slate-300 text-sm">Complete data universe management</p>
              </div>
            </div>

            {/* Call to Action */}
            <div className="text-appear text-appear-delay-4">
              <button 
                onClick={handleCTAClick}
                className="pulse-border relative bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white font-bold py-4 px-12 rounded-full text-xl transition-all duration-300 transform hover:scale-105 shadow-2xl overflow-hidden"
              >
                Enter the Dimension
                <span className="ml-2">→</span>
              </button>
              
              <p className="mt-6 text-slate-400 font-mono text-sm">
                // Prepare for data evolution
              </p>
            </div>

            {/* Bottom Stats */}
            <div className="mt-16 grid grid-cols-3 gap-8 text-center text-appear text-appear-delay-4">
              <div>
                <div className="text-3xl font-bold text-green-400 font-mono">∞TB</div>
                <div className="text-slate-400 text-sm">Data Processed</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-green-400 font-mono">7D</div>
                <div className="text-slate-400 text-sm">Dimensions</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-green-400 font-mono">∞²</div>
                <div className="text-slate-400 text-sm">Possibilities</div>
              </div>
            </div>
          </div>

          {/* Background Grid */}
          <div className="absolute inset-0 opacity-5 pointer-events-none">
            <div 
              className="absolute inset-0" 
              style={{
                backgroundImage: 'radial-gradient(circle, #34681A 1px, transparent 1px)',
                backgroundSize: '50px 50px'
              }}
            ></div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EvaaXPage;